import { useMemo } from 'react';
import { formatDate } from '../../../utils/dateUtils';
import { useAuth } from '../../../context/AuthContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Configure Day.js plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

export const useColumns = () => {
  const { user } = useAuth();
  return useMemo(() => [
    {
      field: '_sort_status',
      headerName: 'Status',
      width: 120,
      sortable: true,
      renderCell: (row) => {
        // For system auctions, show the system auction status
        const status = row.system_auction_status || row.status;
        const getStatusStyle = (status) => {
          switch(status) {
            case 'active':
              return 'bg-green-100 text-green-800';
            case 'completed':
              return 'bg-purple-100 text-purple-800';
            case 'cancelled':
              return 'bg-red-100 text-red-800';
            case 'pending':
              return 'bg-yellow-100 text-yellow-800';
            case 'hidden':
              return 'bg-gray-100 text-gray-800';
            case 'upcoming':
              return 'bg-blue-100 text-blue-800';
            default:
              return 'bg-gray-100 text-gray-800';
          }
        };

        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
            ${getStatusStyle(status)}`}
          >
            {status}
          </span>
        );
      }
    },
    {
      field: 'auction_house_name',
      headerName: 'Auction House',
      width: 150,
      renderCell: (row) => row.auction_house_name || row._auction_house_name || '-'
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (row) => row.name || '-'
    },
    {
      field: 'start_date',
      headerName: 'Date',
      width: 200,
      renderCell: (row) => {
          if (row.system_auction_id) {
            // For system auctions, handle timezone conversion
            const dateTimeStrings = Object.entries(row.daily_times || {}).map(([date, time]) => {
              const [hours, minutes] = time.split(':');
              
              // Create date in auction timezone and convert to user timezone
              const auctionDateTime = dayjs.tz(
                `${date} ${hours}:${minutes}`,
                'YYYY-MM-DD HH:mm',
                row.timezone || 'UTC'
              );
              
              if (!auctionDateTime.isValid()) {
                return 'Invalid time';
              }
              
              const userDateTime = auctionDateTime.tz(row.user_timezone || 'UTC');
              return `${userDateTime.format('M/D/YYYY')} at ${userDateTime.format('h:mm A')}`;
            });
            
            return dateTimeStrings.join(', ');
          }
          
          // For regular auctions, no timezone conversion needed
        const dateTimeStrings = Object.entries(row.daily_times || {}).map(([date, time]) => {
          const [hours, minutes] = time.split(':');
          const hour = parseInt(hours);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const hour12 = hour % 12 || 12;
          return `${formatDate(date, { user })} at ${hour12}:${minutes} ${ampm}`;
        });

        return dateTimeStrings.join(', ');
      }
    },
    {
      field: 'auction_house_currency',
      headerName: 'House Currency',
      width: 120,
      renderCell: (row) => row.auction_house_currency || '-'
    },
    {
      field: 'user_currency',
      headerName: 'Your Currency',
      width: 120,
      renderCell: (row) => row.user_currency || '-'
    },
    {
      field: 'lot_count',
      headerName: 'Lots',
      width: 100,
      align: 'right',
      renderCell: (row) => row.lot_count || 0
    },
    {
      field: 'won_lots',
      headerName: 'Won',
      width: 100,
      align: 'right',
      renderCell: (row) => row.won_lots || 0
    },
    {
      field: 'lost_lots',
      headerName: 'Lost',
      width: 100,
      align: 'right',
      renderCell: (row) => row.lost_lots || 0
    },
    {
      field: 'total_winning_bids',
      headerName: 'Total Winning Bids',
      width: 150,
      align: 'right',
      renderCell: (row) => formatCurrency(row.total_winning_bids || 0, row.auction_house_currency)
    }
  ], []);
};
